import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, FileText, Paperclip, X } from "lucide-react";
import { docTypeLabel, fmtEmailTime } from "../../utils";
import { DownloadLink } from "../common/DownloadLink";
import type { SimEmail } from "../../types";

export interface EmailModalProps {
  e: SimEmail;
  onClose: () => void;
}

/* Modal that shows a full email (headers + raw body + attachments). Used by
   downstream agents and the spine when the user wants the raw source. */
export function EmailModal({ e, onClose }: EmailModalProps): JSX.Element {
  const [openAtt, setOpenAtt] = useState<Record<number, boolean>>({});
  const atts = e.attachments || [];
  useEffect(() => {
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal" onClick={onClose}>
      <div
        className="mbox"
        onClick={(ev) => ev.stopPropagation()}
        style={{ width: "min(820px,100%)" }}
      >
        <div className="mbhd">
          <div style={{ flex: 1 }}>
            <div className="mt">{e.subject}</div>
            <div className="ms mono">
              #{e.id} · {fmtEmailTime(e.time)}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={17} />
          </button>
        </div>
        <div className="scroll" style={{ padding: 18 }}>
          <div className="rawhdr" style={{ marginBottom: 12 }}>
            <span className="rk">From</span>
            <span className="rv">{e.from}</span>
            <span className="rk">To</span>
            <span className="rv">{(e.to || []).join(", ")}</span>
            {(e.cc || []).length > 0 && (
              <>
                <span className="rk">Cc</span>
                <span className="rv">{(e.cc || []).join(", ")}</span>
              </>
            )}
            <span className="rk">Date</span>
            <span className="rv">{fmtEmailTime(e.time)}</span>
          </div>
          <div className="rawbody" style={{ maxHeight: "none" }}>
            {(e.raw || "").split("\n").map((ln, k) =>
              ln.startsWith(">") ? (
                <div key={k} className="q">
                  {ln}
                </div>
              ) : (
                <div key={k}>{ln || "\u00A0"}</div>
              ),
            )}
          </div>
          {atts.length > 0 && (
            <div className="attsec">
              <div className="attshd">
                <Paperclip size={13} /> Attachments · {atts.length}
              </div>
              {atts.map((a, i) => {
                const open = openAtt[i];
                return (
                  <div key={i} className="attcard">
                    <div
                      className="attcardhd"
                      onClick={() => setOpenAtt((s) => ({ ...s, [i]: !open }))}
                    >
                      {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      <FileText size={12} color="var(--muted)" />
                      <span className="afn">{a.filename}</span>
                      <span className="atyp">{docTypeLabel(a.doc_type)}</span>
                      <span className="mono tert" style={{ fontSize: 11 }}>
                        v{a.version}
                      </span>
                      <span style={{ marginLeft: "auto" }}>
                        <DownloadLink doc={a} size={13} label="Download" />
                      </span>
                    </div>
                    {open && (
                      <div className="attbody">
                        {a.content || (
                          <span className="tert">
                            No inline preview — download the file to view it.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
